import { Document, Page, Text, View, StyleSheet } from '@react-pdf/renderer'
import { generatePdfSectionsData, isPdfFieldEmpty } from '../../common/helper'

const styles = StyleSheet.create({
  page: { padding: 20, fontFamily: 'Helvetica' },
  mainHeader: {
    fontSize: 20,
    fontWeight: 'bold',
    marginBottom: 16,
    textAlign: 'center',
  },
  sectionHeader: {
    fontSize: 14,
    color: '#000',
    fontWeight: 'bold',
    paddingTop: 6,
    paddingBottom: 6,
  },
  sectionLabel: {
    fontSize: 11,
    color: '#222',
    fontWeight: 'normal',
  },
  sectionUnderline: {
    borderBottomWidth: 1,
    borderBottomColor: '#bbb',
    marginBottom: 16,
    marginTop: 4,
    width: '100%',
  },
  flexRow: {
    flexDirection: 'row',
    width: '100%',
  },
  masterCol: {
    flex: 1,
    marginRight: 24,
    minWidth: 180,
  },
  qaCol: {
    flex: 1,
    minWidth: 180,
  },
  masterDataRow: {
    marginBottom: 8,
  },
  masterDataItem: {
    marginBottom: 2,
    fontSize: 11,
    color: '#222',
  },
  underline: {
    borderBottomWidth: 1,
    borderBottomColor: '#bbb',
    marginVertical: 4,
    width: '100%',
  },
  masterDataItemRow: {
    marginBottom: 8,
  },
  tableHeader: {
    color: 'rgb(136,136,136)',
    fontSize: 10,
    fontWeight: 'bold',
    minWidth: 120,
    marginBottom: 2,
  },
  tableValue: {
    color: 'rgb(43,45,49)',
    fontSize: 10,
  },
  questionsAnswered: {
    fontSize: 10,
    color: 'rgb(43,45,49)',
    marginBottom: 8,
    fontWeight: 'bold',
  },
  questionText: {
    color: 'rgb(43,45,49)',
    fontSize: 10,
    marginBottom: 8,
    fontWeight: 'bold',
  },
  optionText: {
    color: 'rgb(43,45,49)',
    fontSize: 10,
    marginTop: 1,
  },
  remarksLabel: {
    color: 'rgb(136,136,136)',
    fontSize: 10,
    marginBottom: 4,
    marginTop: 2,
  },
  answerItemWrapper: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 6,
  },
  answerCircle: {
    width: 12,
    height: 12,
    borderRadius: 6,
    border: '1pt solid black',
    marginRight: 6,
    alignItems: 'center',
    justifyContent: 'center',
    display: 'flex',
  },
  commentBox: {
    border: '1pt solid black',
    height: 50,
    padding: 5,
    backgroundColor: '#fff',
  },
  row: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    gap: 16,
  },
  col: {
    flex: 1,
    marginRight: 12,
    minWidth: 200,
  },
  questionWrapper: {
    marginBottom: 10,
  },
  questionBlock: {
    marginBottom: 14,
    paddingLeft: 8,
    borderLeftWidth: 2,
    borderLeftColor: '#eee',
  },
  question: { fontSize: 12, marginTop: 6, fontStyle: 'italic', color: '#333' },
  optionsRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginTop: 4,
    marginBottom: 2,
    gap: 8,
  },
  optionItem: {
    flexDirection: 'row',
    alignItems: 'center',
    marginRight: 12,
    marginBottom: 2,
  },
  radio: {
    fontSize: 13,
    marginRight: 2,
    color: '#222',
  },
  optionLabel: {
    fontSize: 11,
    color: '#222',
  },
  infoIcon: {
    fontSize: 9,
    color: '#888',
    marginTop: 2,
    marginBottom: 4,
    lineHeight: 1.2,
  },
})

const RFI3PdfGenerator = ({ rfi3Template, rfi3Data, rfiMasterData }) => {
  const pdfSectionsData = generatePdfSectionsData(
    rfi3Template,
    rfi3Data,
    rfiMasterData,
  )

  function renderMasterData(value, keyPrefix = '') {
    if (Array.isArray(value)) {
      if (
        value.length > 0 &&
        typeof value[0] === 'object' &&
        value[0] !== null &&
        'label' in value[0] &&
        'value' in value[0]
      ) {
        return (
          <View wrap={false} key={keyPrefix} style={styles.masterDataRow}>
            {value.map((item, itemIdx) => (
              <View style={styles.masterDataItemRow} key={itemIdx}>
                <Text style={styles.tableHeader}>
                  {item.label?.replace(/<\/?br\s*\/?>/gi, '')}
                </Text>
                <Text style={styles.tableValue}>
                  {Array.isArray(item.value)
                    ? item.value.join(', ')
                    : item.value}
                </Text>
              </View>
            ))}
            <View style={styles.underline} />
          </View>
        )
      }
      return value.map((item, idx) =>
        renderMasterData(item, keyPrefix + idx + '-'),
      )
    } else if (
      value &&
      typeof value === 'object' &&
      value.label !== undefined &&
      value.value !== undefined
    ) {
      return (
        <View style={styles.masterDataItemRow} key={keyPrefix}>
          <Text style={styles.tableHeader}>
            {value.label?.replace(/<\/?br\s*\/?>/gi, '')}
          </Text>
          <Text style={styles.tableValue}>
            {Array.isArray(value.value) ? value.value.join(', ') : value.value}
          </Text>
        </View>
      )
    } else if (value && typeof value === 'object') {
      return Object.entries(value).map(([k, v], idx) =>
        renderMasterData(v, keyPrefix + k + '-'),
      )
    }
    return null
  }

  return (
    <Document>
      {pdfSectionsData.map((tab) =>
        tab.data?.map((section, sectionIdx) => {
          const masterData = section.rfi3Data?.masterData
          const filteredMasterData = Object.fromEntries(
            Object.entries(masterData || {}).filter(
              ([, value]) => !isPdfFieldEmpty(value),
            ),
          )
          const hasMasterData =
            filteredMasterData &&
            typeof filteredMasterData === 'object' &&
            Object.keys(filteredMasterData).length > 0

          const questions =
            section.rfi3Data?.rfi3_questions_and_answers?.filter(
              (q) => q.type !== 'fileupload' && q.uid !== 'header',
            ) || []
          const totalQuestions = questions.length
          const answeredQuestions = questions.filter((q) => {
            if (Array.isArray(q.options)) {
              return q.answer && q.options.includes(q.answer)
            }
            return q.answer && String(q.answer).trim() !== ''
          }).length

          if (!hasMasterData) return null

          return (
            <Page size="A4" key={section.sectionId} style={styles.page}>
              {sectionIdx === 0 && rfi3Data?.supplier_name && (
                <View
                  style={{
                    flexDirection: 'row',
                    alignItems: 'center',
                    marginBottom: 12,
                  }}
                >
                  <Text
                    style={{ fontSize: 12, fontWeight: 'bold', marginRight: 6 }}
                  >
                    Supplier Name:
                  </Text>
                  <Text style={{ fontSize: 12 }}>{rfi3Data.supplier_name}</Text>
                </View>
              )}
              {sectionIdx === 0 && (
                <Text style={styles.mainHeader}>{tab.parentName}</Text>
              )}
              <Text style={styles.sectionHeader}>{section.sectionName}</Text>
              {section.sectionLabel && (
                <Text style={styles.sectionLabel}>{section.sectionLabel}</Text>
              )}
              <View style={styles.sectionUnderline} />
              <View style={styles.flexRow}>
                <View style={styles.masterCol}>
                  {Object.entries(masterData).map(([key, value], idx) => {
                    if (
                      value &&
                      typeof value === 'object' &&
                      value.__sectionLabel
                    ) {
                      return (
                        <View key={key + '-section'}>
                          <Text style={styles.sectionHeader}>
                            {value.__sectionLabel}
                          </Text>
                          {Object.entries(value)
                            .filter(([k]) => !k.startsWith('__'))
                            .map(([subKey, subVal], subIdx) => {
                              if (Array.isArray(subVal)) {
                                return subVal.map((row, rowIdx) => (
                                  <View key={subKey + '-' + rowIdx}>
                                    {row?.__subSectionLabel && (
                                      <Text style={styles.sectionLabel}>
                                        {row.__subSectionLabel}{' '}
                                        {row.__rowIndex ? row.__rowIndex : ''}
                                      </Text>
                                    )}
                                    {renderMasterData(
                                      row,
                                      subKey + '-' + rowIdx + '-',
                                    )}
                                  </View>
                                ))
                              }
                              return null
                            })}
                        </View>
                      )
                    }
                    return renderMasterData(value, key + '-')
                  })}
                </View>
                <View style={styles.qaCol}>
                  <Text style={styles.questionsAnswered}>
                    Questions Answered - {answeredQuestions}/{totalQuestions}
                  </Text>
                  {(() => {
                    let questionNumber = 1
                    return section.rfi3Data?.rfi3_questions_and_answers
                      ?.filter((item) => item.type !== 'fileupload')
                      .map((questionAnsItem, index) => {
                        if (questionAnsItem.uid === 'header') {
                          questionNumber = 1 // reset numbering for new header group
                          return (
                            <Text
                              style={styles.questionText}
                              key={`header-${index}`}
                            >
                              {questionAnsItem.question}
                            </Text>
                          )
                        }
                        const number = questionNumber++
                        return (
                          <View
                            style={styles.questionWrapper}
                            key={questionAnsItem.order}
                            wrap={false}
                          >
                            <Text style={styles.questionText}>
                              {`${number}. ${questionAnsItem.question}`}
                            </Text>
                            <View>
                              {questionAnsItem?.options ? (
                                questionAnsItem.options.map((answerOption) => (
                                  <View key={answerOption}>
                                    <View style={styles.answerItemWrapper}>
                                      <View style={styles.answerCircle}>
                                        {questionAnsItem.answer &&
                                          answerOption ===
                                            questionAnsItem.answer && (
                                            <View
                                              style={{
                                                width: 6,
                                                height: 6,
                                                borderRadius: 3,
                                                backgroundColor: '#222',
                                                margin: 'auto',
                                                alignSelf: 'center',
                                                marginTop: 2,
                                              }}
                                            />
                                          )}
                                      </View>
                                      <Text style={styles.optionText}>
                                        {answerOption}
                                      </Text>
                                    </View>
                                  </View>
                                ))
                              ) : (
                                <Text style={styles.optionText}>
                                  {questionAnsItem.answer || ''}
                                </Text>
                              )}
                            </View>
                            <View>
                              <Text style={styles.remarksLabel}>
                                Add Remarks/Notes
                              </Text>
                              <View style={styles.commentBox} />
                            </View>
                          </View>
                        )
                      })
                  })()}
                </View>
              </View>
            </Page>
          )
        }),
      )}
    </Document>
  )
}

export default RFI3PdfGenerator
